import type { ComponentProps } from 'react'
import React, { useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import useOfferListTracking from 'hooks/Offers/useOfferListTracking'
import { getListName } from 'analytics/enhanced-ecommerce'
import { OfferListEventsProvider } from 'components/OfferList/OfferListEventsContext'
import OfferCardCarouselSection from 'components/Common/OfferCardCarousel/OfferCardCarouselSection'
import Places from 'constants/places'
import { getSearchQueryFromFilter } from 'lib/search/searchUtils'
import Pane from 'components/Common/Pane'
import { LUXURY_PLUS } from 'luxPlus/constants/base'
import { getCanViewLuxPlusBenefits, getIsEnabledLuxPlusCruiseInclusions } from 'luxPlus/selectors/featureToggle'
import { connect } from 'react-redux'
import { getIsLuxPlusToursEnabled } from 'checkout/selectors/tourV2Selectors'
import useLuxPlusFreePreviewUtils from 'luxPlus/hooks/useLuxPlusFreePreviewUtils'
import { INCLUSIONS_TITLE_LUXPLUS } from 'constants/inclusions'
import { LabelCountProvider } from 'contexts/LabelCountContext'
import useOfferList from 'hooks/Offers/useOfferList'
import { nonNullable } from 'lib/array/arrayUtils'
import GeoContext from 'contexts/geoContext'
import getRegionSaveAmountCopy from 'luxPlus/lib/getRegionSaveAmountCopy'
import { useExperienceLuxPlusCarouselProps } from 'hooks/Experiences/useExperienceLuxPlusCarouselProps'

export enum LUX_PLUS_OFFER_CAROUSEL_IDS {
  flashOffers = 'discounted_flash',
  hotelInclusions = 'hotel_inclusions',
  earlyAccess = 'early_access',
  hiddenOffers = 'exclusive_offers',
  discountedTours = 'discounted_tours',
  cruiseInclusions = 'cruise_inclusions',
  localExperiences = 'local_experiences',
}

interface MappedProps {
  canViewLuxPlusBenefits: boolean
  isLuxPlusToursEnabled: boolean
  isEnabledLuxPlusCruiseInclusions: boolean
}

interface Props extends MappedProps {
  analyticsPage: string
}

interface SectionProps {
  offerListEventsProvider: ComponentProps<typeof OfferListEventsProvider>
  offerCardCarouselSection: ComponentProps<typeof OfferCardCarouselSection>
}

function LuxPlusOfferCarousels(props: Props) {
  const { analyticsPage, canViewLuxPlusBenefits, isLuxPlusToursEnabled, isEnabledLuxPlusCruiseInclusions } = props

  const intl = useIntl()
  const { currentRegionCode } = useContext(GeoContext)
  const regionSaveAmountCopy = getRegionSaveAmountCopy(currentRegionCode, intl)

  const { showFreePreviewCarouselSortOrder } = useLuxPlusFreePreviewUtils()

  const memberTracking = useOfferListTracking(getListName('OfferList', analyticsPage, 'Member'))
  const memberPriceSectionProps = useMemo<SectionProps>(() => {
    const memberPriceFilter: App.OfferListFilters = {
      offerTypes: ['hotel', 'tactical_ao_hotel'],
      luxPlusFeatures: ['Member prices'],
      destinations: [Places.Anywhere.id],
      sortBy: 'luxPlusList',
      limit: 20,
    }

    return {
      offerListEventsProvider: {
        tracking: memberTracking,
      },
      offerCardCarouselSection: {
        tileSize: 'medium',
        title: intl.formatMessage({
          id: 'c0NPjOfEi4',
          defaultMessage: 'Huge savings on our best hotel offers',
          description: 'Title for LuxPlus member price hotel offers carousel section',
        }),
        subtitle: intl.formatMessage(
          {
            id: 'lpkGsWIh/H',
            defaultMessage: 'Save {regionSaveAmountCopy} every year with additional discounts on our best Limited Time LUX Exclusive offers',
            description: 'Subtitle for LuxPlus member price hotel offers carousel, showing regional savings amount',
          },
          { regionSaveAmountCopy },
        ),
        filters: memberPriceFilter,
        allLink: `/search?${getSearchQueryFromFilter(memberPriceFilter)}`,
        id: LUX_PLUS_OFFER_CAROUSEL_IDS.flashOffers,
        hideWhenEmpty: true,
      },
    }
  }, [memberTracking, regionSaveAmountCopy, intl])

  const earlyAccessTracking = useOfferListTracking(getListName('OfferList', analyticsPage, 'Early access'))
  const earlyAccessSearchFilter = useMemo<App.OfferListFilters>(() => ({
    offerTypes: ['hotel', 'tactical_ao_hotel', 'tour_v2'],
    luxPlusFeatures: ['Early access'],
    destinations: [Places.Anywhere.id],
    sortBy: 'luxPlusList',
    limit: 20,
  }), [])
  const earlyAccessCarouselFilter = useMemo<App.OfferListFilters>(() => ({
    ...earlyAccessSearchFilter,
    sortBy: 'vertical-luxplus',
  }), [earlyAccessSearchFilter])
  const earlyAccessOfferList = useOfferList(earlyAccessCarouselFilter)
  const earlyAccessSectionProps = useMemo<SectionProps>(() => {
    return {
      offerListEventsProvider: {
        tracking: earlyAccessTracking,
      },
      offerCardCarouselSection: {
        tileSize: 'medium',
        title: intl.formatMessage({
          id: 'h4tBT6OEb+',
          defaultMessage: 'Early access to our bestselling offers',
          description: 'Title for LuxPlus early access offers carousel section',
        }),
        subtitle: intl.formatMessage({
          id: 'KVy8cLdRya',
          defaultMessage: 'Take your pick with early access to our Limited Time LUX Exclusive and Exclusive Tour offers before the general public',
          description: 'Subtitle for LuxPlus early access offers carousel section',
        }),
        filters: earlyAccessCarouselFilter,
        allLink: canViewLuxPlusBenefits ? `/search?${getSearchQueryFromFilter(earlyAccessSearchFilter)}` : undefined,
        id: LUX_PLUS_OFFER_CAROUSEL_IDS.earlyAccess,
        hideWhenEmpty: true,
      },
    }
  }, [earlyAccessCarouselFilter, earlyAccessSearchFilter, earlyAccessTracking, canViewLuxPlusBenefits, intl])

  const bonusInclusionsTracking = useOfferListTracking(getListName('OfferList', analyticsPage, 'Bonus inclusions'))
  const bonusInclusionsSectionProps = useMemo<SectionProps>(() => {
    const bonusInclusionFilter: App.OfferListFilters = {
      offerTypes: ['hotel', 'tactical_ao_hotel'],
      luxPlusFeatures: ['Bonus inclusions'],
      destinations: [Places.Anywhere.id],
      sortBy: 'luxPlusList',
      limit: 20,
    }

    return {
      offerListEventsProvider: {
        tracking: bonusInclusionsTracking,
      },
      offerCardCarouselSection: {
        tileSize: 'medium',
        title: intl.formatMessage(
          {
            id: '3mZd9vAjNY',
            defaultMessage: 'Unlock {inclusionsTitle} at select hotels',
            description: 'Title for LuxPlus bonus hotel inclusions carousel section',
          },
          { inclusionsTitle: INCLUSIONS_TITLE_LUXPLUS },
        ),
        subtitle: intl.formatMessage({
          id: 'QqFQkohG5E',
          defaultMessage: 'Make the most of hotel credit, daily breakfast and a complimentary room upgrade*',
          description: 'Subtitle for LuxPlus bonus hotel inclusions carousel section',
        }),
        filters: bonusInclusionFilter,
        allLink: `/search?${getSearchQueryFromFilter(bonusInclusionFilter)}`,
        id: LUX_PLUS_OFFER_CAROUSEL_IDS.hotelInclusions,
        hideWhenEmpty: true,
      },
    }
  }, [bonusInclusionsTracking, intl])

  const memberOnlyTracking = useOfferListTracking(getListName('OfferList', analyticsPage, 'Member only'))
  const memberOnlySectionProps = useMemo<SectionProps>(() => {
    const memberOnlyFilter: App.OfferListFilters = {
      offerTypes: ['hotel', 'tactical_ao_hotel'],
      luxPlusFeatures: ['Member exclusive'],
      destinations: [Places.Anywhere.id],
      scheduleVisibilities: ['luxPlusExclusiveVisible'],
      limit: 20,
    }

    return {
      offerListEventsProvider: {
        tracking: memberOnlyTracking,
      },
      offerCardCarouselSection: {
        tileSize: 'medium',
        title: intl.formatMessage({
          id: 'aCidUMkVhq',
          defaultMessage: 'Unlock hidden hotel offers',
          description: 'Title for LuxPlus member-exclusive hidden hotel offers carousel section',
        }),
        subtitle: intl.formatMessage(
          {
            id: 'thVGKQrQJS',
            defaultMessage: "Unlock hidden hotel offers available exclusively for {programName} members. These escapes aren't available anywhere else",
            description: 'Subtitle for LuxPlus member-exclusive hidden hotel offers carousel section',
          },
          { programName: LUXURY_PLUS.PROGRAM_NAME },
        ),
        filters: memberOnlyFilter,
        allLink: canViewLuxPlusBenefits ? `/search?${getSearchQueryFromFilter(memberOnlyFilter)}` : undefined,
        id: LUX_PLUS_OFFER_CAROUSEL_IDS.hiddenOffers,
        hideWhenEmpty: true,
      },
    }
  }, [canViewLuxPlusBenefits, memberOnlyTracking, intl])

  const tourMemberPricesTracking = useOfferListTracking(getListName('OfferList', analyticsPage, 'Tour member prices'))
  const tourMemberPricesSectionProps = useMemo<SectionProps | undefined>(() => {
    if (!isLuxPlusToursEnabled) return
    const tourMemberPricesFilter: App.OfferListFilters = {
      offerTypes: ['tour_v2'],
      luxPlusFeatures: ['Member prices'],
      destinations: [Places.Anywhere.id],
      sortBy: 'homepage',
      limit: 20,
    }

    return {
      offerListEventsProvider: {
        tracking: tourMemberPricesTracking,
      },
      offerCardCarouselSection: {
        tileSize: 'medium',
        title: intl.formatMessage({
          id: '+6CAdij4+f',
          defaultMessage: 'Save more on Luxury Escapes Tours',
          description: 'Title for LuxPlus discounted tours carousel section',
        }),
        subtitle: intl.formatMessage(
          {
            id: 'WG8kLtAXpH',
            defaultMessage: 'Explore the world and save {regionSaveAmountCopy} on select tours',
            description: 'Subtitle for LuxPlus discounted tours carousel, showing regional savings amount',
          },
          { regionSaveAmountCopy },
        ),
        filters: tourMemberPricesFilter,
        allLink: `/search/tours?${getSearchQueryFromFilter(tourMemberPricesFilter)}`,
        id: LUX_PLUS_OFFER_CAROUSEL_IDS.discountedTours,
        hideWhenEmpty: true,
      },
    }
  }, [tourMemberPricesTracking, isLuxPlusToursEnabled, regionSaveAmountCopy, intl])

  const cruiseBonusInclusionsTracking = useOfferListTracking(getListName('OfferList', analyticsPage, 'Cruise bonus inclusions'))
  const cruiseBonusInclusionsSectionProps = useMemo<SectionProps | undefined>(() => {
    if (!isEnabledLuxPlusCruiseInclusions) return

    const cruiseBonusInclusionsFilter: App.OfferListFilters = {
      offerTypes: ['cruise'],
      destinations: [Places.Anywhere.id],
      sortBy: 'luxPlusList',
      isLuxPlus: true,
      limit: 20,
    }

    return {
      offerListEventsProvider: {
        tracking: cruiseBonusInclusionsTracking,
      },
      offerCardCarouselSection: {
        tileSize: 'medium',
        title: intl.formatMessage({
          id: 'shtW1nJd0x',
          defaultMessage: 'Bonus onboard credit',
          description: 'Title for LuxPlus cruise bonus onboard credit carousel section',
        }),
        subtitle: intl.formatMessage({
          id: 'qe5CkEwwfy',
          defaultMessage: 'Treat yourself with bonus onboard credit on select cruises',
          description: 'Subtitle for LuxPlus cruise bonus onboard credit carousel section',
        }),
        filters: cruiseBonusInclusionsFilter,
        allLink: `/search/cruises?${getSearchQueryFromFilter(cruiseBonusInclusionsFilter)}`,
        id: LUX_PLUS_OFFER_CAROUSEL_IDS.cruiseInclusions,
        hideWhenEmpty: true,
      },
    }
  }, [cruiseBonusInclusionsTracking, isEnabledLuxPlusCruiseInclusions, intl])

  const discountedExperiencesTracking = useOfferListTracking(getListName('OfferList', analyticsPage, 'Discounted experiences'))
  const experienceLuxPlusCarouselProps = useExperienceLuxPlusCarouselProps({
    carouselId: LUX_PLUS_OFFER_CAROUSEL_IDS.localExperiences,
  })
  const discountedExperiencesSectionProps = useMemo<SectionProps | undefined>(() => {
    if (!experienceLuxPlusCarouselProps) return

    return {
      offerListEventsProvider: {
        tracking: discountedExperiencesTracking,
      },
      offerCardCarouselSection: experienceLuxPlusCarouselProps,
    }
  }, [discountedExperiencesTracking, experienceLuxPlusCarouselProps])

  const renderedSectionsProps = useMemo<Array<SectionProps>>(() => {
    let sections = [
      discountedExperiencesSectionProps,
      memberPriceSectionProps,
      earlyAccessSectionProps,
      bonusInclusionsSectionProps,
      tourMemberPricesSectionProps,
      memberOnlySectionProps,
      cruiseBonusInclusionsSectionProps,
    ]

    if (showFreePreviewCarouselSortOrder) {
      sections = [
        discountedExperiencesSectionProps,
        earlyAccessSectionProps,
        memberPriceSectionProps,
        bonusInclusionsSectionProps,
        tourMemberPricesSectionProps,
        memberOnlySectionProps,
        cruiseBonusInclusionsSectionProps,
      ]
    }

    if (earlyAccessOfferList.offerIds.length <= 1) {
      sections = sections.filter((section) => section?.offerCardCarouselSection.id !== 'early_access')
      sections.push(earlyAccessSectionProps)
    }

    return nonNullable(sections)
  }, [
    bonusInclusionsSectionProps,
    cruiseBonusInclusionsSectionProps,
    memberPriceSectionProps,
    earlyAccessOfferList.offerIds.length,
    earlyAccessSectionProps,
    memberOnlySectionProps,
    showFreePreviewCarouselSortOrder,
    tourMemberPricesSectionProps,
    discountedExperiencesSectionProps,
  ])

  return <LabelCountProvider maxLabels={2}>
    <Pane>
      {renderedSectionsProps.map((sectionProps) => (
        <OfferListEventsProvider key={sectionProps.offerCardCarouselSection.id} {...sectionProps.offerListEventsProvider}>
          <OfferCardCarouselSection {...sectionProps.offerCardCarouselSection} />
        </OfferListEventsProvider>
      ))}
    </Pane>
  </LabelCountProvider>
}

const mapStateToProps = (state: App.State): MappedProps => ({
  canViewLuxPlusBenefits: getCanViewLuxPlusBenefits(state),
  isLuxPlusToursEnabled: getIsLuxPlusToursEnabled(state),
  isEnabledLuxPlusCruiseInclusions: getIsEnabledLuxPlusCruiseInclusions(state),
})

export default connect(mapStateToProps)(LuxPlusOfferCarousels)
